import {
  CanvasTexture,
  Group,
  LinearFilter,
  LinearMipmapLinearFilter,
  MeshPhysicalMaterial,
  Object3D,
  SRGBColorSpace,
  type Material,
} from 'three';
import type { Domino } from '../../../engine/dominoes/domino.js';
import {
  assembleTileMesh,
  buildBodyGeometry,
  buildFaceGeometry,
  buildSideGradientTexture,
  setTileDim as kitSetTileDim,
  type TileDims,
} from '../tile-kit.js';

/**
 * Domino tile mesh — a long beveled brick (2 × 1 × 0.26) with a printed
 * face plate on top. Aspect matches a real bone domino (2" × 1" × 3/8")
 * but a touch flatter so the camera reads the face cleanly.
 *
 * Geometry, side AO bake, mesh assembly + dim helper come from
 * `tile-kit.ts`. This module owns the things specific to dominoes:
 *   - polished modern ivory caps (no bone-grain texture — domino tiles
 *     read smoother than mahjong)
 *   - per-tile face canvas with two pip halves and a divider groove
 *   - face-down toggle for the CPU hand + boneyard
 */

export const DOM_LONG = 2.0;
export const DOM_SHORT = 1.0;
export const DOM_D = 0.26;
export const DOM_GAP = 0.05;
export const DOM_PITCH_LONG = DOM_LONG + DOM_GAP;
export const DOM_PITCH_SHORT = DOM_SHORT + DOM_GAP;

const DIMS: TileDims = {
  sizeX: DOM_LONG,
  sizeZ: DOM_SHORT,
  depth: DOM_D,
  cornerRadius: 0.075,
  bevel: 0.022,
};
const FACE_LIFT = 0.0025;

let _bodyGeom: ReturnType<typeof buildBodyGeometry> | null = null;
function bodyGeom() {
  if (!_bodyGeom) _bodyGeom = buildBodyGeometry(DIMS);
  return _bodyGeom;
}

let _faceGeom: ReturnType<typeof buildFaceGeometry> | null = null;
function faceGeom() {
  if (!_faceGeom) _faceGeom = buildFaceGeometry(DIMS);
  return _faceGeom;
}

let _sideGradient: CanvasTexture | null = null;
function sideGradient() {
  if (!_sideGradient) {
    _sideGradient = buildSideGradientTexture({
      topColor: '#ffffff',
      midColor: '#d6d6d6',
      bottomColor: '#7c7c7c',
    });
  }
  return _sideGradient;
}

// ── Material templates ──────────────────────────────────────────────
let _capsMat: MeshPhysicalMaterial | null = null;
function capsMaterial(): MeshPhysicalMaterial {
  if (_capsMat) return _capsMat;
  // Polished modern ivory: warmer than dice-white, glossy clearcoat so the
  // key light catches it. CLAUDE.md calls for "glossy ivory".
  _capsMat = new MeshPhysicalMaterial({
    color: 0xf5ecd6,
    roughness: 0.32,
    metalness: 0,
    clearcoat: 0.6,
    clearcoatRoughness: 0.2,
    sheen: 0.12,
    sheenColor: 0xffeec8,
    sheenRoughness: 0.6,
  });
  return _capsMat;
}

let _sideMat: MeshPhysicalMaterial | null = null;
function sideMaterial(): MeshPhysicalMaterial {
  if (_sideMat) return _sideMat;
  _sideMat = new MeshPhysicalMaterial({
    color: 0xd6c8a4,
    map: sideGradient(),
    roughness: 0.5,
    metalness: 0,
    clearcoat: 0.3,
    clearcoatRoughness: 0.45,
  });
  return _sideMat;
}

// ── Per-tile face texture (pips + divider) ─────────────────────────
const FACE_TEX_W = 1024;
const FACE_TEX_H = 512;
const PIP_FRACTION_RADIUS = 0.085;
const HALF_INSET = 0.12;

type Pt = { x: number; y: number };

function pipPositions(value: number): Pt[] {
  const TL = { x: 0.25, y: 0.25 };
  const TR = { x: 0.75, y: 0.25 };
  const BL = { x: 0.25, y: 0.75 };
  const BR = { x: 0.75, y: 0.75 };
  const ML = { x: 0.25, y: 0.5 };
  const MR = { x: 0.75, y: 0.5 };
  const C = { x: 0.5, y: 0.5 };
  switch (value) {
    case 0: return [];
    case 1: return [C];
    case 2: return [TL, BR];
    case 3: return [TL, C, BR];
    case 4: return [TL, TR, BL, BR];
    case 5: return [TL, TR, C, BL, BR];
    case 6: return [TL, ML, BL, TR, MR, BR];
    default: return [];
  }
}

function drawHalf(ctx: CanvasRenderingContext2D, value: number, x0: number, y0: number, w: number, h: number): void {
  const cell = Math.min(w, h);
  const inset = cell * HALF_INSET;
  const ox = x0 + (w - cell) / 2 + inset;
  const oy = y0 + (h - cell) / 2 + inset;
  const innerSize = cell - inset * 2;
  const r = innerSize * PIP_FRACTION_RADIUS;
  const positions = pipPositions(value);
  for (const p of positions) {
    const cx = ox + p.x * innerSize;
    const cy = oy + p.y * innerSize;
    // Recessed-pip look: dark center with a brighter rim hint at top-left
    // and a soft drop on bottom-right.
    const rg = ctx.createRadialGradient(cx - r * 0.25, cy - r * 0.25, r * 0.1, cx, cy, r);
    rg.addColorStop(0.0, '#404040');
    rg.addColorStop(0.55, '#1a1a1a');
    rg.addColorStop(1.0, '#050505');
    ctx.fillStyle = rg;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(255, 245, 220, 0.18)';
    ctx.beginPath();
    ctx.arc(cx - r * 0.32, cy - r * 0.36, r * 0.32, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function createDominoFaceTexture(domino: Domino): CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = FACE_TEX_W;
  canvas.height = FACE_TEX_H;
  const ctx = canvas.getContext('2d')!;

  const bg = ctx.createLinearGradient(0, 0, 0, FACE_TEX_H);
  bg.addColorStop(0, '#fdf6e2');
  bg.addColorStop(1, '#f1e6c4');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, FACE_TEX_W, FACE_TEX_H);

  const halfW = FACE_TEX_W / 2;
  drawHalf(ctx, domino.low,  0,     0, halfW, FACE_TEX_H);
  drawHalf(ctx, domino.high, halfW, 0, halfW, FACE_TEX_H);

  // Center divider — thin recessed groove with a 1px highlight on the
  // bottom edge so it reads as cut into the surface.
  const cx = FACE_TEX_W / 2;
  const grooveW = 4;
  ctx.fillStyle = 'rgba(20, 18, 14, 0.55)';
  ctx.fillRect(cx - grooveW / 2, FACE_TEX_H * 0.06, grooveW, FACE_TEX_H * 0.88);
  ctx.fillStyle = 'rgba(255, 248, 220, 0.30)';
  ctx.fillRect(cx + grooveW / 2, FACE_TEX_H * 0.06, 1, FACE_TEX_H * 0.88);

  // Sparse speckle for the bone-surface character.
  const img = ctx.getImageData(0, 0, FACE_TEX_W, FACE_TEX_H);
  for (let i = 0; i < img.data.length; i += 4) {
    if (Math.random() < 0.18) continue;
    const n = (Math.random() - 0.5) * 6;
    img.data[i]     = Math.max(0, Math.min(255, img.data[i]! + n));
    img.data[i + 1] = Math.max(0, Math.min(255, img.data[i + 1]! + n * 0.85));
    img.data[i + 2] = Math.max(0, Math.min(255, img.data[i + 2]! + n * 0.6));
  }
  ctx.putImageData(img, 0, 0);

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.anisotropy = 8;
  tex.minFilter = LinearMipmapLinearFilter;
  tex.magFilter = LinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

export function createDominoFaceMaterial(domino: Domino): Material {
  const map = createDominoFaceTexture(domino);
  return new MeshPhysicalMaterial({
    color: 0xffffff,
    map,
    roughness: 0.28,
    metalness: 0,
    clearcoat: 0.55,
    clearcoatRoughness: 0.22,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
}

export function createDominoMesh(faceMaterial: Material): Group {
  const caps = capsMaterial().clone();
  const sides = sideMaterial().clone();
  return assembleTileMesh({
    bodyGeom: bodyGeom(),
    faceGeom: faceGeom(),
    dims: DIMS,
    faceLift: FACE_LIFT,
    capsMat: caps,
    sidesMat: sides,
    faceMat: faceMaterial,
  });
}

/** Hide the face material and reveal a plain ivory back. Used for CPU and
 *  boneyard tiles; the body still renders so the back of the tile shows. */
export function setFaceDown(group: Object3D, faceDown: boolean): void {
  const m = (group.userData.materials as { face?: { visible: boolean } } | undefined);
  if (!m || !m.face) return;
  m.face.visible = !faceDown;
}

export const setTileDim = kitSetTileDim;
